// src/components/auth/ResetPasswordPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, KeyRound } from 'lucide-react';
import authService from '../../services/auth.service';
import OakTreeLoader from '../common/OakTreeLoader';
import logoVideo from '../../logo.mp4';

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        newPassword: '',
        confirmPassword: ''
    });

    // Check if token exists
    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token. Please request a new password reset link.');
        }
    }, [token]);

    const validatePassword = (password) => {
        if (!password) {
            return 'Password is required';
        }
        if (password.length < 8) {
            return 'Password must be at least 8 characters';
        }
        if (!/[A-Z]/.test(password)) {
            return 'Password must contain at least one uppercase letter';
        }
        if (!/[a-z]/.test(password)) {
            return 'Password must contain at least one lowercase letter';
        }
        if (!/[0-9]/.test(password)) {
            return 'Password must contain at least one number';
        }
        return '';
    };

    const validateForm = () => {
        const errors = {
            newPassword: validatePassword(newPassword),
            confirmPassword: ''
        };

        if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (newPassword !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setFieldErrors(errors);
        return !errors.newPassword && !errors.confirmPassword;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!token) {
            setError('Invalid reset token. Please request a new password reset link.');
            return;
        }

        if (!validateForm()) {
            return;
        }

        setError('');
        setLoading(true);

        try {
            await authService.resetPassword(token, newPassword);
            setSuccess(true);
            setTimeout(() => {
                setShowLoader(true);
            }, 2000);
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || 'Failed to reset password';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return { score: 0, text: '' };

        let score = 0;
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;

        const texts = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
        const colors = ['', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500'];

        return { score, text: texts[score], color: colors[score] };
    };

    const strength = getPasswordStrength(newPassword);

    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
                {showLoader ? <OakTreeLoader /> : (
                    <div className="max-w-md w-full bg-gray-900 rounded-lg shadow-xl p-8 border border-gray-700">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-green-900 bg-opacity-30 mb-4">
                                <CheckCircle className="w-8 h-8 text-green-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">Password Reset Successful!</h2>
                            <p className="text-gray-400 mb-6">
                                Your password has been successfully reset. You can now login with your new password.
                            </p>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full py-3 px-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold rounded-md transition-all"
                            >
                                Go to Login
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
            <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left Side - Branding */}
                <div className="hidden lg:flex flex-col items-center justify-center text-center p-8">
                    <div className="w-full max-w-md aspect-video mb-8">
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-contain"
                        >
                            <source src={logoVideo} type="video/mp4" />
                        </video>
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-4">Reset Your Password</h1>
                    <p className="text-gray-400 text-lg">
                        Enter your new password below to complete the reset process.
                    </p>
                </div>

                {/* Right Side - Reset Form */}
                <div className="w-full max-w-md mx-auto">
                    <div className="bg-gray-900 rounded-lg shadow-xl p-8 border border-gray-700">
                        <div className="text-center mb-8">
                            <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-yellow-900 bg-opacity-30 mb-4">
                                <KeyRound className="w-8 h-8 text-yellow-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-white">New Password</h2>
                            <p className="text-gray-400 text-sm mt-2">
                                Create a secure password for your account
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-900 bg-opacity-30 border border-red-700 rounded-md flex items-start">
                                <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                                <p className="text-red-400 text-sm">{error}</p>
                            </div>
                        )}

                        {!token && (
                            <div className="mb-6 p-4 bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded-md">
                                <p className="text-yellow-400 text-sm">
                                    No reset token found.{' '}
                                    <button
                                        onClick={() => navigate('/login')}
                                        className="underline font-medium"
                                    >
                                        Go to login
                                    </button>
                                </p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* New Password */}
                            <div>
                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                                    New Password *
                                </label>
                                <div className="relative">
                                    <input
                                        id="newPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => {
                                            setNewPassword(e.target.value);
                                            setFieldErrors(prev => ({ ...prev, newPassword: '' }));
                                        }}
                                        className={`w-full pl-10 pr-10 py-3 bg-gray-800 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                                            fieldErrors.newPassword ? 'border-red-500' : 'border-gray-600'
                                        }`}
                                        placeholder="Enter new password"
                                        disabled={!token}
                                    />
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {fieldErrors.newPassword && (
                                    <p className="mt-1 text-sm text-red-500">{fieldErrors.newPassword}</p>
                                )}

                                {/* Password Strength Indicator */}
                                {newPassword && (
                                    <div className="mt-2">
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${strength.color} transition-all`}
                                                    style={{ width: `${(strength.score / 5) * 100}%` }}
                                                />
                                            </div>
                                            <span className={`text-xs ${strength.score >= 3 ? 'text-green-500' : 'text-gray-400'}`}>
                                                {strength.text}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Password Requirements */}
                                <div className="mt-2 space-y-1">
                                    <p className="text-xs text-gray-500">Password must contain:</p>
                                    <div className="grid grid-cols-2 gap-1">
                                        <p className={`text-xs ${newPassword.length >= 8 ? 'text-green-500' : 'text-gray-500'}`}>
                                            {newPassword.length >= 8 ? '✓' : '•'} 8+ characters
                                        </p>
                                        <p className={`text-xs ${/[A-Z]/.test(newPassword) ? 'text-green-500' : 'text-gray-500'}`}>
                                            {/[A-Z]/.test(newPassword) ? '✓' : '•'} Uppercase
                                        </p>
                                        <p className={`text-xs ${/[a-z]/.test(newPassword) ? 'text-green-500' : 'text-gray-500'}`}>
                                            {/[a-z]/.test(newPassword) ? '✓' : '•'} Lowercase
                                        </p>
                                        <p className={`text-xs ${/[0-9]/.test(newPassword) ? 'text-green-500' : 'text-gray-500'}`}>
                                            {/[0-9]/.test(newPassword) ? '✓' : '•'} Number
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                                    Confirm Password *
                                </label>
                                <div className="relative">
                                    <input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            setConfirmPassword(e.target.value);
                                            setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
                                        }}
                                        className={`w-full pl-10 pr-10 py-3 bg-gray-800 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                                            fieldErrors.confirmPassword ? 'border-red-500' : 'border-gray-600'
                                        }`}
                                        placeholder="Confirm new password"
                                        disabled={!token}
                                    />
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                                    >
                                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {fieldErrors.confirmPassword && (
                                    <p className="mt-1 text-sm text-red-500">{fieldErrors.confirmPassword}</p>
                                )}
                                {confirmPassword && newPassword === confirmPassword && (
                                    <p className="mt-1 text-sm text-green-500 flex items-center">
                                        <CheckCircle className="w-4 h-4 mr-1" /> Passwords match
                                    </p>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading || !token}
                                className="w-full py-3 px-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>

                        {/* Back to Login */}
                        <div className="mt-6 text-center">
                            <button
                                onClick={() => navigate('/login')}
                                className="text-sm text-yellow-500 hover:text-yellow-400"
                            >
                                ← Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
